// Pure logic for the `time_range` filter control (design doc §4): the fixed
// presets, custom-range validation, and the reader-facing range summary. No
// React/DOM: `filter-bar.tsx` owns the popover and the native `<input>` wiring.
//
// TIMEZONE RULE: every value this module produces or reads is UTC. The
// warehouse compiles against UTC (`service/bi/core/compiler.py`'s DATE()/
// DATE_TRUNC() bounds), and a local-time range would show readers in two zones
// different windows under the same label. A `datetime-local` value carries no
// offset, so this module takes it AS UTC verbatim and the filter bar labels the
// control "(UTC)".
//
// WIRE PRECISION: `FilterState.time_range_start`/`_end` accept a bare day
// ("2026-08-01") OR a moment ("2026-08-01T14:30:00"), told apart by RAW STRING
// SHAPE, never by the parsed clock reading (an explicit midnight is not "no time
// given"). Both bounds must carry the SAME precision: the backend refuses a mixed
// pair (`MixedTimeRangePrecision`), so `isValidCustomRange` refuses one first.

export interface TimeRangePreset {
  readonly label: string;
  /** Days back from today, inclusive of today. `null` = "All time" (clears
   *  the override entirely). */
  readonly days: number | null;
}

/** The preset a dashboard opens on with no `?filters=` in the URL. See
 *  `filter-state.ts`'s `defaultFilterState` for why it is a real window
 *  rather than "All time". */
export const DEFAULT_PRESET_DAYS = 7;

export const TIME_RANGE_PRESETS: readonly TimeRangePreset[] = [
  { label: "Today", days: 0 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "All time", days: null },
];

/** The default window's own name ("7 days"), for telling a reader where a
 *  reset will land them rather than making them find out by pressing it.
 *  Derived from the preset list so it cannot drift from `DEFAULT_PRESET_DAYS`. */
export function defaultRangeLabel(): string {
  return (
    TIME_RANGE_PRESETS.find((p) => p.days === DEFAULT_PRESET_DAYS)?.label ?? "the default range"
  );
}

/** Today minus `days`, as a bare UTC calendar day ("YYYY-MM-DD"). */
export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/** The `(start, end)` a preset resolves to, both bare UTC days. `null` days
 *  (the "All time" preset) clears the override — both bounds `null`. */
export function presetRange(days: number | null): { start: string | null; end: string | null } {
  return days === null
    ? { start: null, end: null }
    : { start: isoDaysAgo(days), end: isoDaysAgo(0) };
}

/** Which preset (if any) the current `(start, end)` pair exactly matches —
 *  drives the pressed/active state on the preset buttons. `null` means the
 *  reader is looking at a custom range no preset produces. */
export function matchingPresetLabel(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  for (const preset of TIME_RANGE_PRESETS) {
    const range = presetRange(preset.days);
    if (range.start === start && range.end === end) return preset.label;
  }
  return null;
}

export type BoundPrecision = "date" | "datetime";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/** The precision a wire bound string carries, or `null` for anything that
 *  matches neither recognized shape (garbage from a hand-edited link).
 *  Mirrors the backend's own dispatch (`compiler.py::_parse_time_bound`) by
 *  RAW STRING SHAPE, never by inspecting a parsed clock reading. */
export function boundPrecision(value: string): BoundPrecision | null {
  if (DATE_ONLY.test(value)) return "date";
  if (DATE_TIME.test(value)) return "datetime";
  return null;
}

/** True iff `start`/`end` form a well-formed custom range the backend will
 *  accept: both present, both a recognized shape, and the SAME precision.
 *  The backend refuses a mixed pair outright (`MixedTimeRangePrecision`) —
 *  there is no honest single reading for "from a bare day through a precise
 *  moment" — so this is checked here too, before a request is ever built. */
export function isValidCustomRange(
  start: string | null | undefined,
  end: string | null | undefined,
): boolean {
  if (!start || !end) return false;
  const startPrecision = boundPrecision(start);
  const endPrecision = boundPrecision(end);
  return startPrecision !== null && startPrecision === endPrecision;
}

/** A local `<input type="date">` (`"YYYY-MM-DD"`) or `<input
 *  type="datetime-local">` (`"YYYY-MM-DDTHH:MM"`) value -> the wire bound.
 *  `datetime-local` carries no seconds; padded to `:00` so the string always
 *  matches `DATE_TIME` above. The value is taken AS UTC verbatim — see the
 *  module's timezone rule. */
export function inputValueToWireBound(value: string, precision: BoundPrecision): string {
  if (precision === "date") return value;
  return value.length === 16 ? `${value}:00` : value;
}

/** The inverse of `inputValueToWireBound`: a wire bound -> the value the
 *  matching native input accepts (trimmed to what that input type reads). */
export function wireBoundToInputValue(
  value: string | null | undefined,
  precision: BoundPrecision,
): string {
  if (!value) return "";
  return precision === "date" ? value.slice(0, 10) : value.slice(0, 16);
}

/** Converts one bound's IN-PROGRESS `<input>` value from one precision's
 *  shape to the other — for the custom-range control's "add a specific
 *  time" toggle, which switches both inputs' TYPE without discarding
 *  whatever the reader already typed. `date -> datetime` defaults the added
 *  clock reading to `00:00` (the reader can still change it before
 *  applying); `datetime -> date` just drops the time portion. Presentation
 *  only — this is never the wire shape (`inputValueToWireBound` is), and an
 *  empty value passes through unchanged either way. */
export function convertInputPrecision(value: string, to: BoundPrecision): string {
  if (!value) return value;
  if (to === "date") return value.slice(0, 10);
  return value.length === 10 ? `${value}T00:00` : value;
}

const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
const MONTH_DAY_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});
const HOUR_MINUTE = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

/** Parses a wire bound as a UTC instant, regardless of precision. The wire
 *  carries no offset, so `Z` is appended explicitly: `Date` reads a no-offset
 *  datetime string as LOCAL time. */
function parseAsUtc(value: string, precision: BoundPrecision): Date {
  return new Date(precision === "date" ? `${value}T00:00:00Z` : `${value}Z`);
}

function formatBound(value: string, precision: BoundPrecision, includeYear: boolean): string {
  const parsed = parseAsUtc(value, precision);
  const day = includeYear ? MONTH_DAY_YEAR.format(parsed) : MONTH_DAY.format(parsed);
  return precision === "date" ? day : `${day}, ${HOUR_MINUTE.format(parsed)}`;
}

/** A short, reader-facing label for the currently-applied range: a preset
 *  match reads as its own name ("Today", "30 days"), "All time" for no
 *  override, and a custom range reads as "Aug 1 to Aug 9" (or, with a
 *  time-of-day override, "Aug 1, 14:30 to Aug 9, 09:00 UTC") — never a dash,
 *  per this app's copy style (root doctrine 1.13). The applied range must be
 *  legible without opening anything, so this is meant to render standing
 *  next to the preset buttons, not just inside the custom-range popover.
 *
 *  The year renders on BOTH bounds or NEITHER, never just the one that
 *  happens to differ from this year: a range formatted "Dec 20, 2025 to
 *  Jan 5" reads as if both fell in 2025, which is exactly the kind of
 *  silently-wrong-scope label this app refuses everywhere else. */
export function describeTimeRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  const preset = matchingPresetLabel(start, end);
  if (preset) return preset;
  if (!start || !end) return "All time";
  const startPrecision = boundPrecision(start);
  const endPrecision = boundPrecision(end);
  if (!startPrecision || !endPrecision) return "Custom range";
  const currentYear = new Date().getUTCFullYear();
  const includeYear =
    parseAsUtc(start, startPrecision).getUTCFullYear() !== currentYear ||
    parseAsUtc(end, endPrecision).getUTCFullYear() !== currentYear;
  const withTime = startPrecision === "datetime" || endPrecision === "datetime";
  const summary = `${formatBound(start, startPrecision, includeYear)} to ${formatBound(end, endPrecision, includeYear)}`;
  return withTime ? `${summary} UTC` : summary;
}
